import React, { FC, useEffect, useState, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getSingleGroup, updateGroupInformation } from '../../../../../../actions/adminActions';
import { ESingleGroupTabs } from '../../../../../../entities/IAdmin';
import { IState } from '../../../../../../reducers';
import { IProfileReducer } from '../../../../../../reducers/profileReducer';
import lazyBlock from '../../../../../../tools/lazyBlock';
import { isPlatformAdmin } from '../../../../../../tools/permissions';
import { Loader } from '../../../../../Common/Loader/Loader';
import { SingleTab, Tabs } from '../../../../../Common/Tabs/Tabs';
import { GroupInformation } from './GroupInformation';
import IntlMessage from '../../../../../Common/IntlMessage';

type GetSingleGroup = ReturnType<typeof getSingleGroup>;
type UpdateGroupInformation = ReturnType<typeof updateGroupInformation>;

const Wrapper = styled.div`
    display: flex;
    max-width: 1280px;
    margin: 2rem auto;
    flex-direction: column;
`;

const NoPermContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const content = {
    [ESingleGroupTabs.Members]: lazyBlock(lazy(() => import('./Tabs/SingleGroupMembers'))),
    [ESingleGroupTabs.ClusterLink]: lazyBlock(lazy(() => import('./Tabs/SingleGroupClusterLink')))
};

export interface IGroupInformation {
    name: string;
    description: string;
    id: string;
    createdAt: string;
}

export interface ISingleGroupProps {
    /** Same page as admin Single Group, without edit actions (opened from e.g. `/groups/:id/details`). */
    readOnly?: boolean;
}

export const SingleGroup: FC<ISingleGroupProps> = ({ readOnly = false }) => {
    const dispatch = useDispatch();
    const { groupId, tabId } = useParams<{ groupId: string, tabId: ESingleGroupTabs}>();
    const [currentTab, setCurrentTab] = useState<ESingleGroupTabs>(ESingleGroupTabs.Members);
    const staticDataTabs: ESingleGroupTabs[] = [ESingleGroupTabs.Members, ESingleGroupTabs.ClusterLink];
    const [groupData, setGroupData] = useState<IGroupInformation>(undefined);
    const [isEditable, setIsEditable] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<boolean>(false);
    const PageContent = readOnly ? content[ESingleGroupTabs.Members] : content[currentTab || ESingleGroupTabs.Members];
    const { currentUserPermissions } = useSelector<IState, IProfileReducer>(state => ({
        ...state.profile
    }));
    useEffect(() => {
        if (groupId) {
            setLoadError(false);
            setCurrentTab(ESingleGroupTabs.Members);
            dispatch<GetSingleGroup>(getSingleGroup(groupId)).then(group => {
                const groupInformation = {
                    name: group.name,
                    description: group.description,
                    id: group.id,
                    createdAt: group.createdAt
                };
                setGroupData(groupInformation);
                setIsEditable(group.isEditable !== false);
            }).catch(() => {
                if (readOnly) {
                    setLoadError(true);
                }
            });
        }
    }, [groupId, readOnly, dispatch]);

    const cancel = () => {
        return new Promise(resolve => {
            try {
                dispatch<GetSingleGroup>(getSingleGroup(groupId)).then(group => {
                    const groupInformation = {
                        name: group.name,
                        description: group.description,
                        id: group.id,
                        createdAt: group.createdAt
                    };
                    setGroupData(groupInformation);
                    setIsEditable(group.isEditable !== false);
                    resolve(true);
                });
            } catch {
                resolve(false);
            }
        });
    };

    const save = (body: {name: string, description: string}) => {
        return new Promise(resolve => {
            try {
                dispatch<UpdateGroupInformation>(updateGroupInformation(groupId, body)).then((group) => {
                    const groupInformation = {
                        name: group.name,
                        description: group.description,
                        id: group.id,
                        createdAt: group.createdAt
                    };
                    setGroupData(groupInformation);
                    setIsEditable(group.isEditable !== false);
                    resolve(true);
                });
            } catch {
                resolve(false);
            }
        });
    };

    useEffect(() => {
        return () => {
            setCurrentTab(ESingleGroupTabs.Members);
        };
    }, []);

    useEffect(() => {
        setCurrentTab(tabId || ESingleGroupTabs.Members);
    }, [tabId]);

    const changeTab = (tab: ESingleGroupTabs) => {
        setCurrentTab(tab);
    };

    if (readOnly) {
        if (loadError) {
            return (
                <Wrapper>
                    <NoPermContainer>
                        <IntlMessage id="admin.noPerm" />
                    </NoPermContainer>
                </Wrapper>
            );
        }
        return (
            <Wrapper>
                <Loader loading={!groupData}>
                    <GroupInformation
                        groupData={groupData}
                        cancel={cancel}
                        save={save}
                        isEditable={false}
                        readOnly
                    />
                    <div>
                        <PageContent isEditable={false} readOnly />
                    </div>
                </Loader>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            {isPlatformAdmin(currentUserPermissions) ? (
                <Loader loading={!groupData}>
                    <GroupInformation groupData={groupData} cancel={cancel} save={save} isEditable={isEditable}/>
                    <div>
                        <Tabs>
                            {staticDataTabs.map(tab =>
                                <SingleTab
                                    key={tab}
                                    active={currentTab === tab}
                                    onClick={() => changeTab(tab)}
                                >
                                    <IntlMessage id={`admin.groups.tab.${tab}`} />
                                </SingleTab>
                            )}
                        </Tabs>
                        <div>
                            <PageContent isEditable={isEditable} />
                        </div>
                    </div>
                </Loader>
            ) : (
                <NoPermContainer>
                    <IntlMessage id="admin.noPerm" />
                </NoPermContainer>
            )}
        </Wrapper>
    );
};

export default SingleGroup;